import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "web-design-system";
import { handleError } from "web-core/utils";
import { useRestartPortfolioReview } from "creso-core/hooks";
import { PortfolioReview } from "creso-core/types";

// Shown on a draft whose holdings have moved since it was built. Dismissing
// hides it until the review is opened again.
export function PortfolioChangedBanner({
  review,
}: {
  review: PortfolioReview;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (
    dismissed ||
    review.status !== "DRAFT" ||
    !review.portfolio_change?.changed
  ) {
    return null;
  }

  return (
    <div
      role="banner"
      className="flex items-center gap-4 rounded-lg border border-border bg-tag-slate p-4"
    >
      <div className="flex min-w-0 flex-1 text-sm flex-col gap-1">
        <span className="font-semibold">
          The portfolio has changed since this review started
        </span>
        <span>
          You may continue working on this version or start a new review using
          the current portfolio.
        </span>
      </div>
      <Button
        variant="outline"
        className="shrink-0"
        onClick={() => setConfirming(true)}
      >
        Start New Review
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dismiss"
        className="shrink-0"
        onClick={() => setDismissed(true)}
      >
        <X />
      </Button>
      <RestartReviewDialog
        review={review}
        open={confirming}
        onOpenChange={setConfirming}
      />
    </div>
  );
}

function RestartReviewDialog({
  review,
  open,
  onOpenChange,
}: {
  review: PortfolioReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { mutate: restart, isPending } = useRestartPortfolioReview();

  const start = () =>
    restart(review, {
      onSuccess: (next) => {
        onOpenChange(false);
        navigate({
          to: "/clients/$id/review/$reviewId",
          params: { id: String(review.client_id), reviewId: String(next.id) },
        });
      },
      onError: (error) => handleError(error, "Could not start a new review"),
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isPending && onOpenChange(next)}
    >
      <DialogContent className="w-120 gap-0 p-0">
        <DialogHeader className="gap-4 p-6 text-left">
          <DialogTitle className="text-lg! mb-0">
            Discard this draft and start new?
          </DialogTitle>
          <DialogDescription className="text-foreground">
            This will update the table with latest holdings. However, your
            recommendations and notes in this draft won’t carry over.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 border-t border-border p-5 sm:space-x-0">
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Continue Editing
          </Button>
          <Button disabled={isPending} onClick={start}>
            {isPending ? <Loader2 className="animate-spin" /> : null}
            Start New Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
